import { Injectable, Signal, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { Dialog } from '@app-core/services/dialog';
import { OverlayRef } from '@app-core/services/overlay';
import { ContractType } from '@app-model/api';
import { CheckPendingDays } from '@app-model/login';
import { Notification } from '@app-shared/components/notification/notification.service';
import { SimpleDialogComponent } from '@app-shared/components/simple-dialog/simple-dialog.component';
import * as fromRoot from '@app-store/reducers';
import * as RemitActions from '@app-store/remit/remit.actions';
import { selectUserHasAccessScope } from '@app-store/user/user.selectors';
import { take, tap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class PendingContractsWarningService {
  private _store$ = inject<Store<fromRoot.State>>(Store);
  private _notification = inject(Notification);
  private _dialog = inject(Dialog);

  private _contracts: Signal<ContractType[]> = toSignal(
    this._store$.select((store) => store.remitState.pendingGridContracts)
  );

  showNotifications(): void {
    this.clearNotifications();
    this._store$
      .select(selectUserHasAccessScope('CONTRACT_READ'))
      .pipe(
        take(1),
        tap((hasContractReadScope) => {
          if (hasContractReadScope) {
            this._store$.dispatch(new RemitActions.GetPendingContractsAction());
            const showWarning30 = this._contracts().some((contract: ContractType) => contract.tagCode === 'A');
            const showWarning45 = this._contracts().some((contract: ContractType) => contract.tagCode === 'B');

            if (showWarning30) {
              this._notification.display({ value: { message: this._getMessage('A') }, duration: 15000 });
            }

            if (showWarning45) {
              this._notification.display({ value: { message: this._getMessage('B') } });
            }
          }
        })
      )
      .subscribe();
  }

  showModal(): void {
    const showModal = this._contracts().some((contract: ContractType) => contract.tagCode === 'C');
    if (showModal) {
      this._showPendingWarningDialog(this._getMessage('C'));
    }
  }

  clearNotifications(): void {
    this._notification.clear();
  }

  private _getMessage(type: string): string {
    const userState = JSON.parse(localStorage.getItem('userState'));
    let message = '';
    userState.user.pendingDays?.checkPendingDays?.forEach((item: CheckPendingDays) => {
      if (item.rangeTagCode === type) {
        message = item.loginMessage;
      }
    });

    return message;
  }

  private _showPendingWarningDialog(innerHtml: string): void {
    this._dialog.open(SimpleDialogComponent, {
      data: {
        actions: [{ text: 'Ok', act: (ref: OverlayRef<SimpleDialogComponent>) => ref.close(), buttonType: 'success' }],
        value: { title: 'Warning!', innerHtml },
      },
    });
  }
}
